import { Link } from "react-router-dom";
import { Button, buttonClass, Callout, Card, DataTable, EmptyState, Badge } from "@promptgatewaysadmin/ui";
import { useInvoices, useSubscriptionState } from "../queries/hooks.js";
import { ErrorState, OfflineBanner, Skeleton } from "../components/states.js";
import { HandoffButton } from "../components/HandoffButton.js";
import { DateText, money } from "../components/format.js";
import { capabilities } from "../api/capabilities.js";
import { getPlan } from "../pricing.js";
import type { SubscriptionState } from "../api/normalise.js";

/**
 * /billing — admin only, and FULLY FUNCTIONAL when the org is locked. These are the screens
 * that unlock the account; routing a locked customer away from them would leave them no way
 * to pay us.
 */
export function Billing() {
  const sub = useSubscriptionState();
  const invoices = useInvoices(true);

  return (
    <div className="pg-page pg-page--narrow">
      <OfflineBanner />

      {sub.data?.status === "past_due" && (
        // Non-dismissible: dunning is the one banner a customer must not be able to make
        // go away without acting on it.
        <Callout tone="warn" title="Your last payment didn't go through" role="status">
          We&apos;ll retry automatically. Update your card to avoid interruption.{" "}
          <HandoffButton label="Update card" size="sm" />
        </Callout>
      )}

      {sub.isError ? (
        <ErrorState error={sub.error} fallbackKey="error.billing.load" onRetry={() => void sub.refetch()} />
      ) : sub.isPending ? (
        <Skeleton height={160} />
      ) : (
        <>
          <PlanCardSection state={sub.data} />
          <PaymentMethodSection state={sub.data} />
        </>
      )}

      <Card title="Invoices">
        {invoices.isError ? (
          // Table-level, not page-level: the plan card, the nav and the shell are unaffected.
          <ErrorState
            error={invoices.error}
            fallbackKey="error.billing.invoices"
            onRetry={() => void invoices.refetch()}
          />
        ) : invoices.isPending ? (
          <Skeleton height={140} />
        ) : (
          <>
            <DataTable
              caption="Invoices"
              rowKey={(r) => r.id}
              rows={invoices.data.pages.flatMap((p) => p.data)}
              columns={[
                {
                  key: "date",
                  header: "Date",
                  rowHeader: true,
                  cell: (r) => <DateText iso={r.created} />,
                },
                { key: "number", header: "Number", cell: (r) => r.number ?? "—" },
                {
                  key: "amount",
                  header: "Amount",
                  numeric: true,
                  // Currency IN THE STRING, never a bare number relying on column position.
                  cell: (r) => money(r.amountDue, r.currency),
                },
                {
                  key: "status",
                  header: "Status",
                  cell: (r) => <Badge tone={r.status === "paid" ? "success" : "warn"}>{r.status}</Badge>,
                },
                {
                  key: "pdf",
                  header: "Receipt",
                  align: "end",
                  // Hidden entirely when there is no URL. A dead link on a billing screen
                  // is worse than no link.
                  cell: (r) =>
                    r.pdfUrl ? (
                      <a href={r.pdfUrl} target="_blank" rel="noopener noreferrer">
                        PDF ↗
                      </a>
                    ) : (
                      "—"
                    ),
                },
              ]}
              empty={
                // An empty table must never look like a failed one.
                <EmptyState
                  title="No invoices yet"
                  description="You haven't been billed. Invoices appear here after your first payment."
                />
              }
            />
            {invoices.hasNextPage && (
              <Button
                variant="secondary"
                disabled={invoices.isFetchingNextPage}
                onClick={() => void invoices.fetchNextPage()}
              >
                {invoices.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function PlanCardSection({ state }: { state: SubscriptionState }) {
  const plan = getPlan(state.plan);
  return (
    <Card title={plan.displayName} eyebrow="Current plan">
      <p className="pg-card__body">
        {state.seatCount} of {state.seatLimit} seats
        {state.interval ? ` · billed ${state.interval === "year" ? "annually" : "monthly"}` : ""}
      </p>
      {state.currentPeriodEnd && (
        <p className="pg-card__body">
          {state.cancelAtPeriodEnd ? "Ends " : "Renews "}
          <DateText iso={state.currentPeriodEnd} />
        </p>
      )}
      <div className="pg-form__actions">
        <HandoffButton label="Manage billing" />
      </div>
    </Card>
  );
}

/**
 * Payment method.
 *
 * Subscription.paymentMethod is a read-only pass-through that has not merged yet, so this
 * renders the specced degraded state — and NEVER invented digits. A fabricated "•••• 4242"
 * on a billing screen is a lie about the customer's own money.
 *
 * When it lands it also unlocks the expiring-card warning, which is the cheapest dunning
 * prevention available: a card that silently expires is a past_due we could have prevented
 * for free.
 */
function PaymentMethodSection({ state }: { state: SubscriptionState }) {
  if (state.status === "trialing") {
    // A trial has no Stripe subscription. The Customer Portal would happily store a card
    // that nothing ever bills — the only way to add one that matters is through checkout.
    return (
      <Card title="Payment method">
        <p className="pg-card__body">
          No card yet. Choose a plan to add one — nothing is charged until you confirm.
        </p>
        <Link className={buttonClass("primary")} to="/checkout">
          Choose a plan
        </Link>
      </Card>
    );
  }
  if (!capabilities.paymentMethod) {
    return (
      <Card title="Payment method">
        <p className="pg-card__body">
          No card on file. Nothing will be charged automatically.
        </p>
        <HandoffButton label="Add a card" />
      </Card>
    );
  }
  return (
    <Card title="Payment method">
      <HandoffButton label="Update card" />
    </Card>
  );
}
